import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { formatDateForReservationUrl } from "@libs/paths";
import type { MutableRefObject } from "react";

/**
 * URLクエリパラメータとフォーム状態を同期するフック
 *
 * ## 背景
 * page_viewイベントはrouter.replaceで発火するため、以下のケースで二重発火を防ぐ必要がある：
 * 1. 初期ロード時：URLにすでにパラメータがある状態での不要な更新
 * 2. 日付変更時：古いmatchIdでの更新（新しいmatchIdが確定する前）
 *
 * ## 解決策
 * - isInitialLoadRef: 初期ロード時のURL更新をスキップ
 * - isMatchIdPendingRef: matchId確定を待つ（useRefで同期的に参照）
 * - URL比較: 変更がない場合はスキップ
 */
const useSyncQueryParams = (
  isReady: boolean,
  selectedDateOptionValue: string | undefined,
  selectedDate: string | undefined,
  selectedMatchId: string | undefined,
  isMatchIdPendingRef: MutableRefObject<boolean>,
  hasNavigationErrorRef: MutableRefObject<boolean>
) => {
  const router = useRouter();
  /** 初期ロード時はURL更新をスキップ（カレンダーからの遷移時にpage_viewが二重発火するのを防ぐ） */
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    // ガード0: エラー発生時は店舗詳細へ遷移するため、URL更新をスキップ
    if (hasNavigationErrorRef.current) {
      return;
    }

    if (!isReady || !selectedDate) {
      return;
    }

    // ガード1: 日付変更直後はmatchIdが未確定のため待機
    // 例: 日付変更 → matchOptionsDataフェッチ中 → まだ古いmatchIdが残っている
    // useRefを参照することで、同じレンダリングサイクル内でも最新の値を参照可能
    if (selectedMatchId === undefined || isMatchIdPendingRef.current) {
      return;
    }

    // ガード2: 初期ロード時はスキップ（カレンダーからの遷移時など）
    // 例: /request_reservation?date=20240101&match=123 で直接アクセス
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    const currentDate = router.query.date as string | undefined;
    const currentMatch = router.query.match as string | undefined;
    const newDate = formatDateForReservationUrl(selectedDate);

    // 空文字列（試合なし）はURLパラメータに含めないためundefinedに変換
    // ガード3でcurrentMatch(undefined)と正しく比較するために必要
    const newMatch = selectedMatchId || undefined;

    // ガード3: URLが変わらない場合はスキップ
    // 例: フォーム内の他の値が変わっただけで日付・試合は同じ
    if (currentDate === newDate && currentMatch === newMatch) {
      return;
    }

    // dateとmatch以外はそのままにしたいので分離する
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { date, match, ...restParams } = router.query;

    router.replace(
      {
        query: {
          ...restParams,
          date: newDate,
          ...(newMatch && { match: newMatch }),
        },
      },
      undefined,
      { shallow: true }
    );
  }, [
    selectedDate,
    selectedDateOptionValue,
    selectedMatchId,
    isMatchIdPendingRef,
    router,
    isReady,
    hasNavigationErrorRef,
  ]);
};

export default useSyncQueryParams;
